import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isArray } from 'lodash';

import { useDebounced, useThrottled } from '../../../utilities/Hooks';
import useSize from '../../../utilities/UseSize';
import { useColumnContentWidth } from '../ColumnContext';
import { useDataStore, useTotalLoadedRows } from '../DataStore';
import {
    useSetTableIsInitialState,
    useSetTableRowBounds,
    useTableIsInitialState,
    useTableRowBounds,
} from '../TableState';
import useScrollListener, { ScrollDirection } from './UseScrollListener';
import useIsPrinting from '../../../utilities/PrintUtilities';

const RenderContext = createContext();

const useRenderContext = () => useContext(RenderContext);
export const useContentHeight = () => useRenderContext().contentHeight;
export const useContentWidth = () => useRenderContext().contentWidth;
export const useViewportHeight = () => useRenderContext().viewportHeight;
export const useViewportWidth = () => useRenderContext().viewportWidth;
export const useRows = () => useRenderContext().renderRows;
export const useRowHeight = () => useRenderContext().rowHeight;

/**
 * This provider is responsible for determining a number of render properties for the
 * infinite scrolling portion of the table. It also handles various state synchronization
 * for PDF printing and when the user scrolls.
 */
export default function RenderContextProvider(props) {
    const { children, rowHeight, shrink, visibleRows, viewportRef } = props;

    const { width: viewportWidth, height: viewportHeight } = useSize(viewportRef);

    const { total: totalRows, isLoading, hasLoaded } = useDataStore();
    const contentWidth = useColumnContentWidth();
    const loadedRows = useTotalLoadedRows();
    const tableRowBounds = useTableRowBounds();
    const isTableInitialState = useTableIsInitialState();
    const setIsTableInitialState = useSetTableIsInitialState();
    const [scrollTop, setScrollTop] = useState(viewportRef.current?.scrollTop);
    const setScrollTopThrottled = useThrottled(setScrollTop, 25);
    const isPrinting = useIsPrinting();

    const isTableRowBoundsValid = isRowBoundsValid(tableRowBounds);

    // used to keep track of the last render things
    const lastRenderArgs = useRef();
    const lastTotalRows = useRef();
    const viewportEl = viewportRef.current;

    // if the number of rows change, we need to manually scroll to the top of the table.
    // Chrome/Edge do this automatically because our sizer changes, but FireFox doesn't.
    // See: DE43547
    useEffect(() => {
        if (!viewportEl || isPrinting) return;

        const currScrollTop = viewportEl.scrollTop;
        const maxScrollTop = rowHeight * totalRows;

        if (!isTableInitialState && currScrollTop > maxScrollTop && viewportEl.scrollTop !== 0) {
            viewportEl.scrollTop = 0;
            setScrollTop(0);
        }

        lastTotalRows.current = totalRows;
    }, [isTableInitialState, rowHeight, totalRows, viewportEl, isPrinting]);

    const { contentHeight, rowBounds } = useMemo(() => {
        let factoryFn = getScrollBasedRenderParams;

        if ((isTableInitialState && isTableRowBoundsValid) || isPrinting) {
            factoryFn = getStateBasedRenderParams;
        }

        if (isLoading && lastRenderArgs.current) {
            factoryFn = () => lastRenderArgs.current;
        }

        return factoryFn({
            loadedRows,
            rowHeight,
            scrollTop,
            shrink,
            tableRowBounds,
            totalRows,
            viewportHeight,
            visibleRows,
            isPrinting,
        });
    }, [
        isLoading,
        isTableInitialState,
        isTableRowBoundsValid,
        loadedRows,
        rowHeight,
        scrollTop,
        shrink,
        tableRowBounds,
        totalRows,
        viewportHeight,
        visibleRows,
        isPrinting,
    ]);

    lastRenderArgs.current = { contentHeight, rowBounds };

    useSyncRowBoundsEffect(rowBounds);

    // Listen for scrolling events and re-render accordingly
    useScrollListener(ScrollDirection.Top, viewportRef, (top) => {
        if (!isTableInitialState && scrollTop !== top) {
            setScrollTopThrottled(top);
        }
    });

    const renderRows = useRenderRowsFromRowBounds(rowBounds);

    // we need to update our state to tell us that we're no longer in our "initial" state
    // after we've loaded all of our data
    useEffect(() => {
        if (isTableInitialState && !isLoading && hasLoaded) {
            setIsTableInitialState(false);
        }
    }, [isTableInitialState, isLoading, hasLoaded, setIsTableInitialState]);

    // this effect ensures we're scrolled to the correct rowBound in state-restoration mode
    useEffect(() => {
        if (isTableInitialState && isTableRowBoundsValid) {
            const [firstRowIdx] = tableRowBounds;
            const scroll = firstRowIdx * rowHeight;

            viewportRef.current.scrollTop = scroll;
            setScrollTop(scroll);
        }
    }, [isTableInitialState, isTableRowBoundsValid, rowHeight, viewportRef, tableRowBounds]);

    const renderState = {
        contentHeight,
        contentWidth,
        viewportHeight,
        viewportWidth,
        renderRows,
        rowHeight,
        visibleRows,
        renderRowBounds: rowBounds,
        shrink,
    };

    return <RenderContext.Provider value={renderState}>{children}</RenderContext.Provider>;
}

export function useRenderLayoutParams() {
    const { rowHeight, visibleRows, shrink, contentHeight, renderRowBounds } = useRenderContext();
    const [firstRowIdx] = renderRowBounds;

    let scrollerHeight = null;

    if (visibleRows > 0) {
        const fullHeight = rowHeight * visibleRows;

        scrollerHeight = shrink ? Math.min(contentHeight, fullHeight) : fullHeight;
    }

    return {
        topOffset: rowHeight * firstRowIdx,
        scrollerHeight,
        visibleRows,
    };
}

/**
 * Calculates the rowBounds and contentHeight for the datatable when we are in scrolling mode
 *
 * @param {Object} options
 */
function getScrollBasedRenderParams(options) {
    const {
        rowHeight,
        visibleRows,
        shrink,
        viewportHeight,
        scrollTop: initialScrollTop,
        loadedRows,
        totalRows,
    } = options;

    let rowBounds;
    const scrollTop = Number.isFinite(initialScrollTop) ? initialScrollTop : 0;

    if (!Number.isFinite(viewportHeight) || viewportHeight === 0) {
        rowBounds = [0, 1];
    }

    // determine row bounds
    const firstRowIdx = Math.floor(scrollTop / rowHeight);
    const lastRowIdx = Math.ceil((scrollTop + viewportHeight) / rowHeight);
    rowBounds = [firstRowIdx, lastRowIdx];

    const minVisibleRows = shrink ? Math.min(visibleRows, totalRows) : visibleRows;
    const contentRows = Math.max(minVisibleRows, loadedRows);
    const contentHeight = contentRows * rowHeight;

    return { rowBounds, contentHeight };
}

/**
 * Calculates the rowBounds and contentHeight for the datatable when we are in state-restoration mode
 *
 * We assume rowBounds is valid. If not, getScrollBasedRenderParams should be used instead.
 *
 * @param {Object} options
 */
function getStateBasedRenderParams(options) {
    const { tableRowBounds, rowHeight, isPrinting, totalRows } = options;

    const [startRowIdx, lastRowIdx] = tableRowBounds;
    const numRows = isPrinting ? Math.min(totalRows, lastRowIdx) - startRowIdx : lastRowIdx;
    const contentHeight = numRows * rowHeight;

    return { contentHeight, rowBounds: tableRowBounds };
}

function isRowBoundsValid(rowBounds) {
    if (!isArray(rowBounds) || rowBounds.length !== 2) return false;

    const [firstRowIdx, lastRowIdx] = rowBounds;

    if (!Number.isFinite(firstRowIdx) || !Number.isFinite(lastRowIdx)) return false;

    if (firstRowIdx < 0 || lastRowIdx < firstRowIdx) return false;

    return true;
}

function useSyncRowBoundsEffect(scrollBasedRowBounds) {
    const rowBounds = useTableRowBounds();
    const setRowBounds = useDebounced(useSetTableRowBounds(), 250);
    const [firstRowIdx, lastRowIdx] = scrollBasedRowBounds;
    const isPrinting = useIsPrinting();

    const [currentFirstRowIdx, currentLastRowIdx] = rowBounds;

    // we need to synchronize the rowBounds with the table state manager
    useEffect(() => {
        if (!isPrinting && (currentFirstRowIdx !== firstRowIdx || currentLastRowIdx !== lastRowIdx)) {
            setRowBounds([firstRowIdx, lastRowIdx]);
        }
    }, [isPrinting, firstRowIdx, lastRowIdx, currentFirstRowIdx, currentLastRowIdx, setRowBounds]);
}

/**
 * Hook to return the rows to render based on row boundaries
 *
 * @param {[int, int]} rowBounds
 */
function useRenderRowsFromRowBounds(rowBounds) {
    const { rows } = useDataStore();
    const [firstRowIdx, lastRowIdx] = rowBounds;

    return useMemo(() => buildRenderRows(firstRowIdx, lastRowIdx, rows), [firstRowIdx, lastRowIdx, rows]);
}

function buildRenderRows(firstRowIdx, lastRowIdx, rows) {
    const renderRows = [];

    for (let i = firstRowIdx; i < lastRowIdx; i++) {
        const wrap = rows[i];

        if (wrap) {
            renderRows.push(wrap);
        }
    }

    return renderRows;
}
